import { useReducer } from 'react';

import type { QueryBuilderAction, QueryBuilderState } from '../../../types/QueryBuilder';
import { PRESENT_VALUE_SENTINEL } from '../../../types/QueryBuilder';
import {
  PRESENT_COMPATIBLE_OPERATORS,
  removeToken,
  replaceToken,
  SINGLE_VALUE_OPERATORS,
  stringifyToken,
  unwrapExpression,
} from '../utils';

export function reducer(state: QueryBuilderState, action: QueryBuilderAction): QueryBuilderState {
  switch (action.type) {
    case 'UPDATE_FILTER_KEY': {
      if (action.token.type !== 'FILTER') {
        return state;
      }
      const updatedToken = {
        ...action.token,
        field: action.key,
        ...(action.newOp !== undefined ? { operator: action.newOp } : {}),
      };
      const newText = stringifyToken(updatedToken);
      const query = replaceToken(state.query, action.token, newText);
      return { ...state, query, focusOverride: action.focusOverride ?? null };
    }
    case 'UPDATE_FILTER_OP': {
      if (action.token.type !== 'FILTER') {
        return state;
      }
      // Regex operators (// and !//) carry the regex content wrapped in literal slashes
      // (e.g. /^foo$/) so the slash delimiters are visible in the query string and value
      // cell. Switching INTO a regex op wraps the existing scalar/multi value in slashes
      // — pushing an empty // when there's nothing to wrap. Switching OUT strips the
      // wrapping slashes so the bare value survives the operator change.
      const isRegex = action.op === '//' || action.op === '!//';
      const wasRegex = action.token.operator === '//' || action.token.operator === '!//';
      let newValue = action.token.value;
      // Drop the `__present__` sentinel when transitioning to an operator
      // that doesn't accept it (numeric comparisons, regex, starts with/ends with).
      // The consumer (LozengeFilter.handleSelect) opens the value-cell popover
      // right after this dispatch so the user can supply a real value.
      const isPresentSentinel =
        newValue === PRESENT_VALUE_SENTINEL ||
        (Array.isArray(newValue) && newValue.length === 1 && newValue[0] === PRESENT_VALUE_SENTINEL);
      if (isPresentSentinel && !PRESENT_COMPATIBLE_OPERATORS.has(action.op)) {
        newValue = '';
      } else if (isRegex && !wasRegex) {
        const inner = Array.isArray(newValue) ? newValue.map(String).join(',') : String(newValue ?? '');
        newValue = `/${inner}/`;
      } else if (!isRegex && wasRegex && typeof newValue === 'string') {
        if (newValue.length >= 2 && newValue.startsWith('/') && newValue.endsWith('/')) {
          newValue = newValue.slice(1, -1);
        }
      }
      // Single-value operators (numeric comparisons) can only carry one value — the backend 400s
      // on multiple, and "is less than or equal to one of [a, b]" is meaningless. Collapse a
      // multi-value selection to its first entry when switching in, so the value cell renders a
      // single value and the operator label drops the "… one of" suffix.
      if (SINGLE_VALUE_OPERATORS.has(action.op) && Array.isArray(newValue)) {
        newValue = newValue.length > 0 ? newValue[0] : '';
      }
      const newText = stringifyToken({ ...action.token, operator: action.op, value: newValue });
      const query = replaceToken(state.query, action.token, newText);
      return { ...state, query, focusOverride: action.focusOverride ?? null };
    }
    case 'UPDATE_FILTER_VALUE': {
      if (action.token.type !== 'FILTER') {
        return state;
      }
      const newText = stringifyToken({ ...action.token, value: action.value });
      const query = replaceToken(state.query, action.token, newText);
      return { ...state, query, focusOverride: action.focusOverride ?? null };
    }
    case 'DELETE_TOKEN': {
      const query = removeToken(state.query, action.token);
      return { ...state, query, focusOverride: action.focusOverride ?? null };
    }
    case 'UNWRAP_EXPRESSION': {
      const query = unwrapExpression(state.query, action.token);
      return { ...state, query, focusOverride: action.focusOverride ?? null };
    }
    case 'UPDATE_EXPRESSION_TYPE': {
      if (action.token.type !== 'EXPRESSION') {
        return state;
      }
      const updatedToken = { ...action.token, expressionType: action.expressionType };
      const newText = stringifyToken(updatedToken);
      const query = replaceToken(state.query, action.token, newText);
      return { ...state, query, focusOverride: action.focusOverride ?? null };
    }
    case 'APPEND_TEXT': {
      const query = state.query ? `${state.query} ${action.text}` : action.text;
      return { ...state, query, focusOverride: action.focusOverride ?? null };
    }
    case 'SET_QUERY':
      return { ...state, query: action.query, focusOverride: action.focusOverride ?? null };
    case 'UPDATE_QUERY':
      // Used by undo — sets both query and committedQuery so the restored state is
      // immediately committed (no separate COMMIT_QUERY needed).
      return {
        ...state,
        query: action.query,
        committedQuery: action.query,
        focusOverride: action.focusOverride ?? null,
      };
    case 'COMMIT_QUERY':
      return { ...state, committedQuery: state.query };
    case 'RESET_FOCUS_OVERRIDE':
      return { ...state, focusOverride: null };
    case 'REPLACE_SELECTION': {
      if (action.tokens.length === 0) {
        return { ...state, focusOverride: action.focusOverride ?? null };
      }
      const sorted = [...action.tokens].sort((a, b) => a.location.start.offset - b.location.start.offset);
      const rangeStart = sorted[0].location.start.offset;
      const rangeEnd = sorted[sorted.length - 1].location.end.offset;
      const before = state.query.substring(0, rangeStart);
      const after = state.query.substring(rangeEnd);
      const query = (before + action.replacement + after).replace(/\s+/g, ' ').trim();
      return { ...state, query, focusOverride: action.focusOverride ?? null };
    }
    case 'WRAP_SELECTION': {
      if (action.tokens.length === 0) {
        return state;
      }
      const sorted = [...action.tokens].sort((a, b) => a.location.start.offset - b.location.start.offset);
      const rangeStart = sorted[0].location.start.offset;
      const rangeEnd = sorted[sorted.length - 1].location.end.offset;
      const inner = state.query.substring(rangeStart, rangeEnd);
      const before = state.query.substring(0, rangeStart);
      const after = state.query.substring(rangeEnd);
      const query = `${before}(${inner.trim()})${after}`.replace(/\s+/g, ' ').trim();
      return { ...state, query };
    }
    default:
      return state;
  }
}

export function useQueryBuilderState(initialQuery: string) {
  const [state, dispatch] = useReducer(reducer, {
    query: initialQuery,
    committedQuery: initialQuery,
    focusOverride: null,
  });

  return { state, dispatch };
}
